import ApiClient from 'api-client/ApiClient'
import { generateApiErrorPayload } from 'api-client/helpers'
import PicklistField from 'core/components/validatedForm/DropdownField'
import Dropdown from 'core/elements/dropdown'
import { DropdownProps } from 'core/elements/dropdown/Dropdown'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import { useCallback, useMemo, useState } from 'react'
import Text from 'core/elements/Text'
import { styled } from '@material-ui/styles'

const { keystone } = ApiClient.getInstance()

const Container = styled('div')({
  display: 'grid',
  gap: 16,
})

interface UpdateImageAdminEndpointProps {
  onClose: () => void
  servicesList: any[]
  imageAdminEndpoints: string[]
  reload: (showLoader?: boolean, forceReload?: boolean) => Promise<void>
}

export default function UpdateImageAdminEndpoint({
  onClose,
  servicesList,
  imageAdminEndpoints,
  reload,
}: UpdateImageAdminEndpointProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { params, getParamsUpdater } = useParams({
    endpoint: '',
  })

  // Get glance admin endpoint
  const { imageAdminEndpointId, imageAdminUrl } = useMemo(() => {
    const glanceEndpoint = servicesList.find((item) => item.name === 'glance-admin')
    return {
      imageAdminEndpointId: glanceEndpoint?.id,
      imageAdminUrl: glanceEndpoint?.url,
    }
  }, [servicesList])

  // Get glance admin endpoints
  const filteredEndpointsOptions = useMemo(() => {
    return (imageAdminEndpoints || [])
      .filter((item) => item !== imageAdminUrl)
      .map((endpoint) => ({
        label: endpoint,
        value: endpoint,
      }))
  }, [imageAdminEndpoints, imageAdminUrl])

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true)
      await keystone.updateImageAdminEndpoint(imageAdminEndpointId, {
        endpoint: {
          url: params.endpoint,
        },
      })
      reload(true, true)
      onClose()
    } catch (error) {
      setError(generateApiErrorPayload(error, 'Error updating image admin endpoint'))
    } finally {
      setLoading(false)
    }
  }, [params, onClose, imageAdminEndpointId, reload])

  return (
    <ModalForm
      title="Update Endpoint"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={loading}
      error={error}
      submitTitle={`Update`}
      panel="dialog"
      onBackdropClick={null}
      open
    >
      <Container>
        <Text variant="body2">Please select an new endpoint for image admin service</Text>
        <Text variant="body2">
          Current endpoint: <b>{imageAdminUrl}</b>
        </Text>
        <PicklistField
          DropdownComponent={EndpointPicklist}
          id="endpoint"
          onChange={(value) => getParamsUpdater('endpoint')(value)}
          value={params.endpoint as string}
          items={filteredEndpointsOptions}
          label=""
          required
        />
      </Container>
    </ModalForm>
  )
}

export function EndpointPicklist({
  name = 'endpoint',
  label = 'Endpoint',
  compact = false,
  ...rest
}: DropdownProps<string>) {
  return <Dropdown compact={compact} name={name} label={label} {...rest} />
}
